import type { ReactNode } from 'react';

import {
  Box,
  Collapse,
  Flex,
  Heading,
  IconButton,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import type { BoxProps } from '@chakra-ui/react';

import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';

interface SectionPanelProps extends Omit<BoxProps, 'title'> {
  title?: string;
  headerContent?: ReactNode;
  count?: string;
  action?: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  variant?: 'card' | 'plain';
  accent?: boolean;
  children?: ReactNode;
}

const SectionPanel = ({
  title,
  headerContent,
  count,
  action,
  collapsible = false,
  defaultOpen = true,
  variant = 'card',
  accent = false,
  children,
  ...rest
}: SectionPanelProps) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen });

  const cardBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.500', 'gray.400');

  const isCard = variant === 'card';

  const header = (
    <Flex justify="space-between" align="center" minH="40px" gap="4">
      <Flex align="center" gap="3" minW={0}>
        {collapsible && (
          <IconButton
            aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
            icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
            onClick={onToggle}
            variant="ghost"
            size="sm"
            color={labelColor}
          />
        )}
        {headerContent ||
          (title && (
            <Heading size="md" color={textColor} fontWeight="700">
              {title}
            </Heading>
          ))}
        {count && (
          <Text color={labelColor} fontSize="sm">
            {count}
          </Text>
        )}
      </Flex>
      {action}
    </Flex>
  );

  const hasHeader = Boolean(title || headerContent || action || collapsible);
  const headerMargin = !collapsible || isOpen ? '4' : '0';

  const body = collapsible ? (
    <Collapse in={isOpen}>{children}</Collapse>
  ) : (
    children
  );

  if (!isCard) {
    return (
      <Box {...rest}>
        {hasHeader && <Box mb={headerMargin}>{header}</Box>}
        {body}
      </Box>
    );
  }

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      shadow="sm"
      overflow={accent ? 'hidden' : undefined}
      {...rest}
    >
      {accent && (
        <Box h="3px" bg="linear-gradient(135deg, #E8683A 0%, #F47B4A 100%)" />
      )}
      <Box p="5">
        {hasHeader && <Box mb={headerMargin}>{header}</Box>}
        {body}
      </Box>
    </Box>
  );
};

export default SectionPanel;
